// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { zodResolver } from '@hookform/resolvers/zod';
import { useStepsData } from '@shared/ui-kit';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import {
  type LegalDeclarationsFormData,
  legalDeclarationsSchema,
} from '@shared/advisory/account-workflow/types';
import {
  getTaxResidenceDefaultsFromPersonalData,
  resolveLegalDeclarationsFormValues,
} from '@shared/advisory/account-workflow/utils/legal-declarations-defaults';

export const useLegalDeclarations = () => {
  const {
    markStepAsCompleted,
    isStepCompleted,
    getFormData,
    saveFormData,
    saveAccountOpeningData,
    isUpdatingClientOnboardingProfile,
  } = useAccountWorkflowData();

  const { next } = useStepsData();

  const { t, i18n } = useTranslation();

  const savedData = getFormData('legalDeclarations');
  const personalData = getFormData('personalData');

  const formDefaultValues = useMemo(
    () => resolveLegalDeclarationsFormValues(savedData, personalData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const form = useForm<LegalDeclarationsFormData>({
    resolver: zodResolver(legalDeclarationsSchema),
    mode: 'onChange',
    defaultValues: formDefaultValues,
  });

  const legalDeclarations = useWatch({
    control: form.control,
  }) as LegalDeclarationsFormData;

  const taxResidenceCountryOptions = useMemo(() => {
    const items = t('countries', {
      ns: 'inversis-countries',
      returnObjects: true,
    }) as Record<string, string>;

    return Object.entries(items)
      .filter(([value]) => value !== 'united-states')
      .map(([value, label]) => ({
        value,
        label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language));
  }, [i18n.language, t]);

  const yesNoOptions = useMemo(
    () => [
      {
        value: false,
        label: t('account-opening.legal-declarations.common-options.no', {
          ns: 'components',
        }),
      },
      {
        value: true,
        label: t('account-opening.legal-declarations.common-options.yes', {
          ns: 'components',
        }),
      },
    ],
    [t],
  );

  const investmentOwnershipOptions = useMemo(
    () => [
      {
        value: 'self',
        label: t(
          'account-opening.legal-declarations.investment-ownership.options.self',
          { ns: 'components' },
        ),
      },
      {
        value: 'other-person',
        label: t(
          'account-opening.legal-declarations.investment-ownership.options.other-person',
          { ns: 'components' },
        ),
      },
    ],
    [t],
  );

  const relationshipOptions = useMemo(
    () => [
      {
        value: 'spouse-partner',
        label: t(
          'account-opening.legal-declarations.public-person-relationship.detail.relationship.options.spouse-partner',
          { ns: 'components' },
        ),
      },
      {
        value: 'parent',
        label: t(
          'account-opening.legal-declarations.public-person-relationship.detail.relationship.options.parent',
          { ns: 'components' },
        ),
      },
      {
        value: 'child',
        label: t(
          'account-opening.legal-declarations.public-person-relationship.detail.relationship.options.child',
          { ns: 'components' },
        ),
      },
      {
        value: 'sibling',
        label: t(
          'account-opening.legal-declarations.public-person-relationship.detail.relationship.options.sibling',
          { ns: 'components' },
        ),
      },
      {
        value: 'close-associate',
        label: t(
          'account-opening.legal-declarations.public-person-relationship.detail.relationship.options.close-associate',
          { ns: 'components' },
        ),
      },
    ],
    [t],
  );

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const onUsTaxResidenceChange = useCallback(
    (value: boolean) => {
      const defaults = getTaxResidenceDefaultsFromPersonalData(
        getFormData('personalData'),
      );

      form.setValue('isUsTaxResidence', value, { shouldValidate: true });

      if (value) {
        form.setValue('taxResidenceCountry', undefined, { shouldValidate: true });
        form.setValue('taxResidenceDocumentNumber', '', { shouldValidate: true });
        return;
      }

      form.setValue('taxResidenceCountry', defaults.taxResidenceCountry, {
        shouldValidate: true,
      });
      form.setValue(
        'taxResidenceDocumentNumber',
        defaults.taxResidenceDocumentNumber,
        { shouldValidate: true },
      );
      form.setValue('taxResidenceDocumentNumberEdited', false, {
        shouldDirty: false,
      });
    },
    [form, getFormData],
  );

  const onTaxResidenceDocumentNumberChange = useCallback(
    (value: string) => {
      form.setValue('taxResidenceDocumentNumber', value, { shouldValidate: true });
      form.setValue('taxResidenceDocumentNumberEdited', true, {
        shouldDirty: true,
      });
    },
    [form],
  );

  const onNext = useCallback(
    async (data: LegalDeclarationsFormData) => {
      if (isStepCompleted('legal-declarations', form.formState.isDirty)) {
        next();
        return;
      }

      saveFormData('legalDeclarations', data);

      await saveAccountOpeningData({
        submitToInversis: false,
        updatedFormData: { legalDeclarations: data },
      });

      markStepAsCompleted('legal-declarations');

      next();
    },
    [
      form.formState.isDirty,
      markStepAsCompleted,
      next,
      saveAccountOpeningData,
      saveFormData,
      isStepCompleted,
    ],
  );

  useEffect(() => {
    if (!legalDeclarations.isPoliticallyExposedPerson) {
      form.setValue('pepDetail', undefined, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [form, legalDeclarations.isPoliticallyExposedPerson]);

  useEffect(() => {
    if (!legalDeclarations.isRelatedToPublicPerson) {
      form.setValue('publicPersonRelationshipDetail', undefined, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [form, legalDeclarations.isRelatedToPublicPerson]);

  useEffect(() => {
    if (legalDeclarations.investmentOwnership === 'self') {
      form.setValue('beneficialOwnerDetail', undefined, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [form, legalDeclarations.investmentOwnership]);

  return {
    t,
    form,
    onNext,
    isFormValid,
    isUpdatingClientOnboardingProfile,
    legalDeclarations,
    onUsTaxResidenceChange,
    onTaxResidenceDocumentNumberChange,
    taxResidenceCountryOptions,
    yesNoOptions,
    investmentOwnershipOptions,
    relationshipOptions,
  };
};
